import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AppMessagesContext, Localization, SessionContext, actionSetErrorMessage } from "@viamap/viamap2-common";
import { ADModalBody, ADModalFooter, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassButton, GlassButtonRow } from "src/components/GlassButtons";
import { GlassInputControl, GlassInputGroup, GlassTextInputTransformer } from "src/components/GlassInput";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { useFloatingPortalContext } from "src/componentsUtils/PortalHelpers";
import { LayerInfo, LayerType } from "src/common/managers/Types";
import { openWindow, WindowId } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { PLAElementRecord, PLAProject, PLARelationRecord, AttributeValue, PLAValueType, PLAAttributeDefinition, hasPLAAdminAccess, isPLANumberValueType } from "./PLAState";
import { getSortedPLAAttributeEntries } from "./PLAAttributeDefinitions";
import { explainPLAExpression, getPLAAlertDetails, PLAExpressionExplanation, resolvePLACalculatedAttributes } from "./PLAExpression";
import { formatPLAAttributeValue } from "./PLAFormatting";

const PLA_ELEMENT_NAME_MAPPING_TARGET = "__plaElementName";

type GeometryAttributeMapping = { sourceAttribute: string; enabled: boolean; targetId: string };
type GeometryAttributeMappingState = { layerName: string; properties: Record<string, any>; mappings: GeometryAttributeMapping[] };
type GeometryAttributeMappingTarget = { id: string; label: string; definition?: PLAAttributeDefinition };

function elementDraftSignature(element: PLAElementRecord) {
   const { _version, ...draftFields } = element;
   return JSON.stringify(draftFields);
}

function getFirstFeatureFromLayer(layer: LayerInfo): any {
   return layer.geoJson?.features?.[0] || layer.geoJson;
}

function isPolygonGeometry(geometry: any) {
   return geometry?.type === "Polygon" || geometry?.type === "MultiPolygon";
}

function collectLayerPolygonCoordinates(layer: LayerInfo): any[] {
   const features = layer.geoJson?.type === "FeatureCollection" ? layer.geoJson.features || [] : [getFirstFeatureFromLayer(layer)];
   return features.flatMap((feature: any) => {
      const geometry = feature?.geometry || feature;
      if (geometry?.type === "Polygon") return [geometry.coordinates];
      if (geometry?.type === "MultiPolygon") return geometry.coordinates || [];
      return [];
   });
}

function getGeometryFromLayer(layer: LayerInfo): any {
   const polygonCoordinates = collectLayerPolygonCoordinates(layer);
   if (polygonCoordinates.length === 1) return { type: "Polygon", coordinates: polygonCoordinates[0] };
   if (polygonCoordinates.length > 1) return { type: "MultiPolygon", coordinates: polygonCoordinates };
   const feature = getFirstFeatureFromLayer(layer);
   return feature?.geometry || feature;
}

function defaultValueForType(valueType: PLAValueType | undefined) {
   if (isPLANumberValueType(valueType)) return "";
   if (valueType === "boolean") return false;
   return "";
}

function copyGeometryFromLayer(layer: LayerInfo, element: PLAElementRecord): PLAElementRecord {
   const geometry = getGeometryFromLayer(layer);
   if (!geometry) return element;
   if (geometry.type === "Point") {
      return { ...element, geoPoint: geometry.coordinates };
   }
   if (isPolygonGeometry(geometry)) {
      return { ...element, geoShape: geometry };
   }
   return element;
}

function getLayerFeatureProperties(layer: LayerInfo): Record<string, any> {
   const properties = getFirstFeatureFromLayer(layer)?.properties;
   return properties && typeof properties === "object" && !Array.isArray(properties) ? properties : {};
}

function convertGeometryAttributeValue(value: any, valueType: PLAValueType | undefined) {
   if (value === undefined || value === null) return "";
   if (isPLANumberValueType(valueType)) {
      if (typeof value === "number") return value;
      const numericText = String(value).trim()
         .replace(/\s*(m2|m²|ha|kr|m)$/i, "")
         .trim();
      if (!numericText) return "";
      const normalizedText = numericText.includes(",")
         ? numericText.replace(/\./g, "").replace(",", ".")
         : numericText.replace(/,/g, "");
      const parsedValue = Number(normalizedText);
      return Number.isFinite(parsedValue) ? parsedValue : "";
   }
   if (valueType === "boolean") return value === true || value === "true" || value === 1 || value === "1";
   return String(value);
}

function GeometryAttributeMappingDialog(props: {
   state: GeometryAttributeMappingState;
   targets: GeometryAttributeMappingTarget[];
   onChange: (sourceAttribute: string, changes: Partial<GeometryAttributeMapping>) => void;
   onApply: () => void;
   onClose: () => void;
}) {
   return (
      <AdvancedDragModal title={Localization.getText("Map geometry attributes")} PosDefault={{ width: "720px", height: "520px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={props.onClose}>
         <ADModalBody>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
               <strong>{props.state.layerName}</strong>
               <table style={{ width: "100%" }}>
                  <thead>
                     <tr>
                        <th style={{ textAlign: "left" }}>{Localization.getText("Map attribute checkbox")}</th>
                        <th style={{ textAlign: "left" }}>{Localization.getText("Layer attribute")}</th>
                        <th style={{ textAlign: "left" }}>{Localization.getText("Geometry attribute value")}</th>
                        <th style={{ textAlign: "left" }}>{Localization.getText("Entity attribute")}</th>
                     </tr>
                  </thead>
                  <tbody>
                     {props.state.mappings.map((mapping) => (
                        <tr key={mapping.sourceAttribute}>
                           <td>
                              <input type="checkbox" checked={mapping.enabled} onChange={(event) => props.onChange(mapping.sourceAttribute, { enabled: event.target.checked })} />
                           </td>
                           <td>{mapping.sourceAttribute}</td>
                           <td>{String(props.state.properties[mapping.sourceAttribute] ?? "")}</td>
                           <td>
                              <select disabled={!mapping.enabled} value={mapping.targetId} onChange={(event) => props.onChange(mapping.sourceAttribute, { targetId: event.target.value })}>
                                 {props.targets.map((target) => (
                                    <option key={target.id} value={target.id}>{target.label}</option>
                                 ))}
                              </select>
                           </td>
                        </tr>
                     ))}
                  </tbody>
               </table>
            </div>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton onClick={props.onApply}>{Localization.getText("Apply geometry attribute mapping")}</GlassButton>
               <GlassButton onClick={props.onClose}>{Localization.getText("Cancel geometry attribute mapping")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}

export function shouldShowPLAElementAttribute(definition: PLAAttributeDefinition, isNewElement: boolean) {
   return definition.type === "constant" || !isNewElement;
}

export function createEmptyPLAElement(project: PLAProject, elementTypeId: string): PLAElementRecord {
   const definitions = project.elementTypes[elementTypeId]?.attributes || {};
   const attributes = Object.entries(definitions).reduce<Record<string, AttributeValue>>((acc, [attributeId, definition]) => {
      if (definition.type === "constant") {
         acc[attributeId] = { mode: "constant", valueType: definition.valueType || "string", value: defaultValueForType(definition.valueType) };
      }
      return acc;
   }, {});
   return {
      recordType: "ELEMENT",
      id: crypto.randomUUID(),
      elementType: elementTypeId,
      name: project.elementTypes[elementTypeId]?.name || elementTypeId,
      attributes
   };
}

export function PLAElementDialog(props: {
   project: PLAProject;
   element: PLAElementRecord;
   elements: PLAElementRecord[];
   relations: PLARelationRecord[];
   availableLayers: LayerInfo[];
   readOnly: boolean;
   onClose: () => void;
   onSave: (element: PLAElementRecord) => void;
   onCreateRelation: (element: PLAElementRecord) => void;
   onDeleteRelation: (relationId: string) => void;
}) {
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: windowDispatch } = useWindowContext();
   const { root: floatingPortalRoot } = useFloatingPortalContext();
   const [element, setElement] = useState<PLAElementRecord>(props.element);
   const [explanation, setExplanation] = useState<PLAExpressionExplanation | { expression: string; error: string; warningMessages?: string[] } | undefined>();
   const [geometryMapping, setGeometryMapping] = useState<GeometryAttributeMappingState | undefined>();
   const lastPersistedElement = useRef(props.element);
   const draftElement = useRef(element);
   useEffect(() => {
      draftElement.current = element;
   }, [element]);
   useEffect(() => {
      const remoteRecordChanged = props.element.id !== lastPersistedElement.current.id || elementDraftSignature(props.element) !== elementDraftSignature(lastPersistedElement.current);
      if (!remoteRecordChanged) return;
      const localDraftChanged = elementDraftSignature(draftElement.current) !== elementDraftSignature(lastPersistedElement.current);
      if (remoteRecordChanged && localDraftChanged) appMessageDispatch(actionSetErrorMessage(Localization.getText("PLA record externally changed edits lost")));
      lastPersistedElement.current = props.element;
      setElement(props.element);
   }, [appMessageDispatch, props.element]);
   const definitions = props.project.elementTypes[element.elementType]?.attributes || {};
   const calculated = useMemo(() => {
      return resolvePLACalculatedAttributes(element, props.project, props.elements, props.relations, { includeErrors: true });
   }, [element, props.project, props.elements, props.relations]);
   const currentRelations = props.relations.filter((relation) => relation.fromId === element.id || relation.toId === element.id);
   const isNewElement = !props.elements.some((item) => item.id === element.id);
   const editableDefinitions = getSortedPLAAttributeEntries(definitions).filter(([, definition]) => shouldShowPLAElementAttribute(definition, isNewElement));
   const availableLayers = useMemo(() => [...props.availableLayers].sort((a, b) => String(a.datasetname || a.filename || a.layerId).localeCompare(String(b.datasetname || b.filename || b.layerId))), [props.availableLayers]);
   const geometryMappingTargets = useMemo<GeometryAttributeMappingTarget[]>(() => [
      { id: PLA_ELEMENT_NAME_MAPPING_TARGET, label: Localization.getText("Name") },
      ...getSortedPLAAttributeEntries(definitions)
         .filter(([, definition]) => definition.type === "constant")
         .map(([attributeId, definition]) => ({ id: attributeId, label: definition.name || attributeId, definition }))
   ], [definitions]);
   const alertsByAttributeId = useMemo(() => getPLAAlertDetails(element, props.project, props.elements, props.relations).reduce<Record<string, string[]>>((acc, alert) => {
      acc[alert.attributeId] = [...(acc[alert.attributeId] || []), alert.message];
      return acc;
   }, {}), [element, props.project, props.elements, props.relations]);
   const canExplainExpressions = hasPLAAdminAccess(props.project, sessionState.userRef, sessionState.customerRef);
   const warningFieldStyle = { background: "#f8d7da", color: "#842029", padding: 4, borderRadius: 2 };

   const setConstantAttribute = (attributeId: string, value: any) => {
      const definition = definitions[attributeId];
      setElement((prev) => ({
         ...prev,
         attributes: {
            ...prev.attributes,
            [attributeId]: { mode: "constant", valueType: definition.valueType || "string", value }
         }
      }));
   };
   const setConstantAttributeFromInput = (attributeId: string, value: string) => {
      const definition = definitions[attributeId];
      setConstantAttribute(attributeId, isPLANumberValueType(definition.valueType) ? (value === "" ? "" : Number(value)) : value);
   };
   const importGeometryFromLayer = (layer: LayerInfo) => {
      setElement((prev) => copyGeometryFromLayer(layer, prev));
      const properties = getLayerFeatureProperties(layer);
      const firstTargetId = geometryMappingTargets[0]?.id || "";
      const propertyNames = Object.keys(properties).sort((a, b) => a.localeCompare(b));
      if (propertyNames.length && geometryMappingTargets.length) {
         setGeometryMapping({
            layerName: layer.datasetname || layer.filename || String(layer.layerId),
            properties,
            mappings: propertyNames.map((sourceAttribute) => ({ sourceAttribute, enabled: false, targetId: firstTargetId }))
         });
      }
   };
   const updateGeometryMapping = (sourceAttribute: string, changes: Partial<GeometryAttributeMapping>) => {
      setGeometryMapping((prev) => prev ? {
         ...prev,
         mappings: prev.mappings.map((mapping) => mapping.sourceAttribute === sourceAttribute ? { ...mapping, ...changes } : mapping)
      } : prev);
   };
   const applyGeometryAttributeMapping = () => {
      if (!geometryMapping) return;
      setElement((prev) => {
         const nextAttributes = { ...prev.attributes };
         let nextName = prev.name;
         geometryMapping.mappings.filter((mapping) => mapping.enabled && mapping.targetId).forEach((mapping) => {
            const sourceValue = geometryMapping.properties[mapping.sourceAttribute];
            if (mapping.targetId === PLA_ELEMENT_NAME_MAPPING_TARGET) {
               nextName = String(convertGeometryAttributeValue(sourceValue, "string"));
               return;
            }
            const definition = definitions[mapping.targetId];
            if (definition?.type !== "constant") return;
            nextAttributes[mapping.targetId] = {
               mode: "constant",
               valueType: definition.valueType || "string",
               value: convertGeometryAttributeValue(sourceValue, definition.valueType)
            };
         });
         return { ...prev, name: nextName, attributes: nextAttributes };
      });
      setGeometryMapping(undefined);
   };

   const geometryStatus = (present: boolean) => present ? Localization.getText("Present") : Localization.getText("Not present");
   const openExpressionExplanation = (attributeId: string, expression: string) => {
      const warningMessages = alertsByAttributeId[attributeId] || [];
      try {
         const nextExplanation = explainPLAExpression(expression || "0", { element, elements: props.elements, relations: props.relations, project: props.project, calculated });
         setExplanation(warningMessages.length ? { ...nextExplanation, lines: [...nextExplanation.lines, "", `${Localization.getText("Warning")}:`, ...warningMessages.map((message) => `  ${message}`)] } : nextExplanation);
      } catch (error: any) {
         setExplanation({ expression, error: error.message || String(error), warningMessages });
      }
   };

   return (<>
      <AdvancedDragModal title={Localization.getText("Element editor")} PosDefault={{ width: "640px", left: "50%", transform: "translateX(-50%)", top: "80px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={() => props.onSave(element)} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Name")}>
                  <input disabled={props.readOnly} value={element.name} onChange={(event) => setElement({ ...element, name: event.target.value })} />
               </GlassTextInputTransformer>
               {!props.readOnly && <GlassTextInputTransformer label={Localization.getText("Geometry from layer")}>
                  <select value="" onChange={(event) => {
                     const layer = props.availableLayers.find((item) => String(item.layerId) === event.target.value);
                     if (layer) importGeometryFromLayer(layer);
                  }}>
                     <option value="">{Localization.getText("Select a data layer")}</option>
                     {availableLayers.filter((layer) => [LayerType.GeoJSON_Point, LayerType.GeoJSON_Polygon, LayerType.GeoJSON].includes(layer.type)).map((layer) => (
                        <option key={layer.layerId} value={layer.layerId}>{layer.datasetname || layer.filename || layer.layerId}</option>
                     ))}
                  </select>
               </GlassTextInputTransformer>}
               <GlassTextInputTransformer label={Localization.getText("Geo point")}>
                  <span>{geometryStatus(Boolean(element.geoPoint))}</span>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Geo shape")}>
                  <span>{geometryStatus(Boolean(element.geoShape))}</span>
               </GlassTextInputTransformer>
               {editableDefinitions.map(([attributeId, definition]) => (
                  <GlassTextInputTransformer key={attributeId} label={definition.name || attributeId}>
                     <div style={alertsByAttributeId[attributeId]?.length ? warningFieldStyle : undefined} title={alertsByAttributeId[attributeId]?.join("\n") || undefined}>
                        <GlassInputControl>
                           {definition.type === "constant" ? (
                              <input
                                 disabled={props.readOnly}
                                 type={isPLANumberValueType(definition.valueType) ? "number" : definition.valueType === "date" ? "date" : "text"}
                                 value={(element.attributes[attributeId] as any)?.value ?? ""}
                                 onChange={(event) => setConstantAttributeFromInput(attributeId, event.target.value)}
                              />
                           ) : (
                              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                 <span>{formatPLAAttributeValue(calculated[attributeId], definition)}</span>
                                 <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                    <small style={{ flex: 1 }}>{definition.expression}</small>
                                    {canExplainExpressions && <GlassButton onClick={() => openExpressionExplanation(attributeId, definition.expression || "0")}>{Localization.getText("Explain")}</GlassButton>}
                                 </div>
                              </div>
                           )}
                        </GlassInputControl>
                     </div>
                  </GlassTextInputTransformer>
               ))}
            </GlassInputGroup>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
               <h4 style={{ margin: 0 }}>{Localization.getText("Relations")}</h4>
               {!props.readOnly && (
                  <GlassButton
                     title={Localization.getText("Create relation")}
                     aria-label={Localization.getText("Create relation")}
                     onClick={() => props.onCreateRelation(element)}
                     style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                  >
                     <MIBIcon icon="mapLink" />
                  </GlassButton>
               )}
            </div>
            <table style={{ width: "100%" }}>
               <tbody>
                  {currentRelations.map((relation) => (
                     <tr key={relation.id}>
                        <td>{props.project.relationTypes[relation.relationType]?.name || relation.relationType}</td>
                        <td>{relation.fromId === element.id ? Localization.getText("Outgoing") : Localization.getText("Incoming")}</td>
                        <td>{props.elements.find((item) => item.id === (relation.fromId === element.id ? relation.toId : relation.fromId))?.name}</td>
                        <td>{!props.readOnly && (
                           <GlassButton
                              title={Localization.getText("Delete relation")}
                              aria-label={Localization.getText("Delete relation")}
                              onClick={() => props.onDeleteRelation(relation.id)}
                              style={{ width: 30, minWidth: 30, height: 28, padding: 0, display: "inline-flex", alignItems: "center", justifyContent: "center" }}
                           >
                              <MIBIcon icon="delete" />
                           </GlassButton>
                        )}</td>
                     </tr>
                  ))}
               </tbody>
            </table>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton onClick={() => windowDispatch(openWindow(WindowId.PLADocuments, { project: props.project, element, elements: props.elements, relations: props.relations }))}>{Localization.getText("Documents")}</GlassButton>
               {!props.readOnly && <GlassButton onClick={() => props.onSave(element)}>{Localization.getText("Save")}</GlassButton>}
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
         {explanation && (
            <AdvancedDragModal title={Localization.getText("Expression explanation")} PosDefault={{ width: "720px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={() => setExplanation(undefined)}>
               <ADModalBody>
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                     <strong>{explanation.expression}</strong>
                     {"error" in explanation ? (
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{[explanation.error, ...(explanation.warningMessages?.length ? ["", `${Localization.getText("Warning")}:`, ...explanation.warningMessages.map((message) => `  ${message}`)] : [])].join("\n")}</pre>
                     ) : (
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>{explanation.lines.join("\n")}</pre>
                     )}
                  </div>
               </ADModalBody>
               <ADModalFooter>
                  <GlassButtonRow>
                     <GlassButton onClick={() => setExplanation(undefined)}>{Localization.getText("Close")}</GlassButton>
                  </GlassButtonRow>
               </ADModalFooter>
            </AdvancedDragModal>
         )}
      </AdvancedDragModal>
      {geometryMapping ? createPortal(
         <GeometryAttributeMappingDialog state={geometryMapping} targets={geometryMappingTargets} onChange={updateGeometryMapping} onApply={applyGeometryAttributeMapping} onClose={() => setGeometryMapping(undefined)} />,
         floatingPortalRoot
      ) : null}
   </>
   );
}

export function PLARelationDialog(props: {
   project: PLAProject;
   sourceElement: PLAElementRecord;
   elements: PLAElementRecord[];
   onClose: () => void;
   onSave: (relation: PLARelationRecord, newElement?: PLAElementRecord) => void;
}) {
   const [relationType, setRelationType] = useState(Object.keys(props.project.relationTypes)[0] || "");
   const targetElements = props.elements.filter((element) => element.id !== props.sourceElement.id);
   const firstTargetElement = targetElements[0];
   const firstElementTypeId = Object.keys(props.project.elementTypes)[0] || "";
   const [targetMode, setTargetMode] = useState<"existing" | "new">(firstTargetElement ? "existing" : "new");
   const [targetElementTypeId, setTargetElementTypeId] = useState(firstTargetElement?.elementType || firstElementTypeId);
   const visibleTargetElements = targetElements.filter((element) => element.elementType === targetElementTypeId);
   const [targetElementId, setTargetElementId] = useState(visibleTargetElements[0]?.id || firstTargetElement?.id || "");
   const existingElementTypeOptions = Object.entries(props.project.elementTypes).filter(([elementTypeId]) => targetElements.some((element) => element.elementType === elementTypeId));
   const [newElement, setNewElement] = useState<PLAElementRecord>(() => createEmptyPLAElement(props.project, firstElementTypeId));
   const newElementDefinitions = props.project.elementTypes[newElement.elementType]?.attributes || {};
   const editableNewElementDefinitions = getSortedPLAAttributeEntries(newElementDefinitions).filter(([, definition]) => shouldShowPLAElementAttribute(definition, true));
   const setNewElementConstantAttribute = (attributeId: string, value: any) => {
      const definition = newElementDefinitions[attributeId];
      setNewElement((prev) => ({
         ...prev,
         attributes: {
            ...prev.attributes,
            [attributeId]: { mode: "constant", valueType: definition.valueType || "string", value }
         }
      }));
   };
   const setNewElementConstantAttributeFromInput = (attributeId: string, value: string) => {
      const definition = newElementDefinitions[attributeId];
      setNewElementConstantAttribute(attributeId, isPLANumberValueType(definition.valueType) ? (value === "" ? "" : Number(value)) : value);
   };
   const saveRelation = () => {
      const targetId = targetMode === "new" ? newElement.id : targetElementId;
      props.onSave({ recordType: "RELATION", id: crypto.randomUUID(), relationType, fromId: props.sourceElement.id, toId: targetId }, targetMode === "new" ? newElement : undefined);
   };

   return (
      <AdvancedDragModal title={Localization.getText("Create relation")} PosDefault={{ width: "520px", left: "50%", transform: "translateX(-50%)", top: "120px" }} onClose={props.onClose}>
         <ADModalBody>
            <GlassInputGroup onSubmit={saveRelation} onEscape={props.onClose}>
               <GlassTextInputTransformer label={Localization.getText("Relation type")}>
                  <select value={relationType} onChange={(event) => setRelationType(event.target.value)}>
                     {Object.entries(props.project.relationTypes).map(([id, relation]) => <option key={id} value={id}>{relation.name || id}</option>)}
                  </select>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Target mode")}>
                  <select value={targetMode} onChange={(event) => setTargetMode(event.target.value as "existing" | "new")}>
                     <option value="existing" disabled={targetElements.length === 0}>{Localization.getText("Select existing element")}</option>
                     <option value="new">{Localization.getText("Create new element")}</option>
                  </select>
               </GlassTextInputTransformer>
               <GlassTextInputTransformer label={Localization.getText("Target element type")}>
                  <select value={targetMode === "new" ? newElement.elementType : targetElementTypeId} onChange={(event) => {
                     const nextElementTypeId = event.target.value;
                     if (targetMode === "new") {
                        setNewElement(createEmptyPLAElement(props.project, nextElementTypeId));
                        return;
                     }
                     const nextTarget = targetElements.find((element) => element.elementType === nextElementTypeId);
                     setTargetElementTypeId(nextElementTypeId);
                     setTargetElementId(nextTarget?.id || "");
                  }}>
                     {(targetMode === "new" ? Object.entries(props.project.elementTypes) : existingElementTypeOptions).map(([id, elementType]) => <option key={id} value={id}>{elementType.name || id}</option>)}
                  </select>
               </GlassTextInputTransformer>
               {targetMode === "existing" ? (
                  <GlassTextInputTransformer label={Localization.getText("Target element")}>
                     <select value={targetElementId} onChange={(event) => setTargetElementId(event.target.value)}>
                        {visibleTargetElements.map((element) => <option key={element.id} value={element.id}>{element.name}</option>)}
                     </select>
                  </GlassTextInputTransformer>
               ) : (
                  <>
                     <GlassTextInputTransformer label={Localization.getText("Name")}>
                        <input value={newElement.name} onChange={(event) => setNewElement({ ...newElement, name: event.target.value })} />
                     </GlassTextInputTransformer>
                     {editableNewElementDefinitions.map(([attributeId, definition]) => (
                        <GlassTextInputTransformer key={attributeId} label={definition.name || attributeId}>
                           <input
                              type={isPLANumberValueType(definition.valueType) ? "number" : definition.valueType === "date" ? "date" : "text"}
                              value={(newElement.attributes[attributeId] as any)?.value ?? ""}
                              onChange={(event) => setNewElementConstantAttributeFromInput(attributeId, event.target.value)}
                           />
                        </GlassTextInputTransformer>
                     ))}
                  </>
               )}
            </GlassInputGroup>
         </ADModalBody>
         <ADModalFooter>
            <GlassButtonRow>
               <GlassButton disabled={!relationType || (targetMode === "existing" && !targetElementId) || (targetMode === "new" && !newElement.elementType)} onClick={saveRelation}>{Localization.getText("Save")}</GlassButton>
               <GlassButton onClick={props.onClose}>{Localization.getText("Close")}</GlassButton>
            </GlassButtonRow>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
